/* eslint-disable @next/next/no-img-element */
import React, { forwardRef, useEffect, useRef, useState } from 'react';

import { useDraggable } from '@dnd-kit/core';
import clsx from 'clsx';
import uniqid from 'uniqid';

// Hooks
import useLibraryMedia from '@/utils/hooks/swr/useLibraryMedia';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';
import { useNewFunnelActivated } from '@/utils/scheduler/funnel/hooks/useNewFunnelActivated';

import useDarkMode from '@/hooks/utils/useDarkMode';

import PropTypes from 'prop-types';

//lib
import { TYPE_REEL, TYPE_VIDEO } from '@/lib/scheduler/postType';
import { INSTAGRAM, TIKTOK_BUSINESS } from '@/lib/socialProfile';

// Components
import Dropdown from '@/components/base/Dropdown';
import Icon from '@/components/base/Icon';
import InfoBanner from '@/components/base/InfoBanner';
import Loader from '@/components/base/Loader';
import LoadMoreButton from '@/components/composite/Button/LoadMoreButton';
import { RadioGroup } from '@/components/composite/Form/RadioGroup';
import ContentIconBg from '@/components/icons/ContentIconBg';
import { TooltipDisableSortableItem } from '@/components/pages/scheduler/feed/TooltipDisableSortableItem';
import MediaUpload from '@/components/pages/media/components/MediaUpload';

const SIZE_DEFAULT = 'default';
const SIZE_SMALL = 'small';

const ImageMedia = ({ media, isDragging = false }) => {
    const { isDarkMode } = useDarkMode();
    const [errorImg, setErrorImage] = useState(false);

    if (errorImg) {
        return (
            <div className="flex h-full w-full flex-1 items-center justify-center bg-icoGray-300 dark:bg-icoDarkMode-anthracite">
                {/* Change Icon to real fallback */}
                <Icon
                    icon="content"
                    className="text-4xl text-icoGray-500 dark:text-icoDarkMode-wolf"
                />
            </div>
        );
    }
    return (
        <div
            className={clsx('relative w-full overflow-hidden rounded-m', {
                'opacity-50': isDragging
            })}>
            <img
                src={
                    media?.min_thumbnail ??
                    media?.image ??
                    media?.thumbnail ??
                    media?.data?.medias[0]?.min_thumbnail ??
                    media?.data?.medias[0]?.image ??
                    media?.data?.medias[0]?.thumbnail
                }
                alt=""
                className="h-full w-full object-cover"
                onError={() => setErrorImage(true)}
            />
            {media?.type === 'video' ? (
                <>
                    <ContentIconBg
                        className="absolute -right-0.5 -top-0.5 z-1"
                        darkMode={isDarkMode()}
                    />
                    <Icon
                        icon="video"
                        className="absolute right-1 top-1 z-2 text-xxs text-icoGray-500 dark:text-icoDarkMode-wolf"
                    />
                </>
            ) : null}
        </div>
    );
};

ImageMedia.propTypes = {
    media: PropTypes.object,
    isDragging: PropTypes.bool
};

const Filters = ({ setRequestParams, className }) => {
    const { t } = useTranslation('scheduler');
    const { activeSocialProfile } = useActiveSocialProfile();
    const network = activeSocialProfile?.social_network_type;
    const [currentFilters, setCurrentFilters] = useState({
        status: '',
        media_types: ''
    });

    const listStatuses = [
        {
            label: t('media_library.media_library_filters.all'),
            value: ''
        },
        {
            label: t('media_library.media_library_filters.used'),
            value: 'used'
        },
        {
            label: t('media_library.media_library_filters.unused'),
            value: 'unused'
        }
    ];

    const listMediaTypes = [
        {
            label: t('media_library.media_library_filters.all'),
            value: ''
        },
        {
            label: t('media_library.media_library_filters.photo'),
            value: 'photo'
        },
        {
            label: t('media_library.media_library_filters.video'),
            value: 'video'
        }
    ];

    return (
        <div className={clsx('flex w-full p-5', className)}>
            {network !== TIKTOK_BUSINESS ? (
                <div className="w-1/2">
                    <p className="mb-3 text-xs">{t('media_library.media_type')}</p>
                    <RadioGroup
                        values={listMediaTypes}
                        columnList
                        className="mb-0"
                        labelClassName="text-xs text-icoBlack dark:text-white font-GMedium"
                        current={currentFilters.media_types}
                        onChange={(e) => {
                            setCurrentFilters((prevState) => ({
                                ...prevState,
                                media_types: e.target.value
                            }));
                            setRequestParams((prevState) => ({
                                ...prevState,
                                media_types: e.target.value === '' ? [] : [e.target.value]
                            }));
                        }}
                    />
                </div>
            ) : null}

            <div
                className={clsx({
                    'w-1/2': network !== TIKTOK_BUSINESS,
                    'w-full': network === TIKTOK_BUSINESS
                })}>
                <p className="mb-3 text-xs">{t('media_library.status')}</p>
                <RadioGroup
                    values={listStatuses}
                    columnList
                    className="mb-0"
                    labelClassName="text-xs text-icoBlack dark:text-white font-GMedium"
                    current={currentFilters.status}
                    onChange={(e) => {
                        setCurrentFilters((prevState) => ({
                            ...prevState,
                            status: e.target.value
                        }));
                        setRequestParams((prevState) => {
                            const finalState = { ...prevState };

                            if (e.target.value === '') {
                                delete finalState.used;
                                return finalState;
                            }

                            return {
                                ...finalState,
                                used: e.target.value === 'used'
                            };
                        });
                    }}
                />
            </div>
        </div>
    );
};

Filters.propTypes = {
    setRequestParams: PropTypes.func
};

const MediaItem = forwardRef(({ media, caption, id, disabled }, ref) => {
    const { isNewFunnel } = useNewFunnelActivated();
    const idGenerated = useRef(uniqid(`draggable-media-${id}-`));
    const { activeSocialProfile } = useActiveSocialProfile();

    // Change type of video to reel for IG video
    const isInstagramReel =
        activeSocialProfile.social_network_type === INSTAGRAM && media?.type === TYPE_VIDEO;
    const postType = isInstagramReel ? TYPE_REEL : media?.type;

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: idGenerated.current,
        disabled,
        data: {
            type: postType,
            data: {
                caption: caption,
                medias: [media],
                share_to_feed: isInstagramReel ? true : undefined
            }
        }
    });

    return (
        <div ref={ref}>
            <div
                className="square draggable-media"
                data-testid={`scheduler-media-library-item-${media.library_media_id}`}
                data-media={isNewFunnel ? JSON.stringify({ media, id }) : JSON.stringify(media)}
                data-post-type={postType}
                data-caption={caption}
                ref={setNodeRef}
                {...listeners}
                {...attributes}>
                <ImageMedia media={media} isDragging={isDragging} />
            </div>
        </div>
    );
});

MediaItem.propTypes = {
    media: PropTypes.object,
    caption: PropTypes.string,
    id: PropTypes.number
};

const MediaList = ({
    mutateLibraryMedia,
    dataLibraryMedia,
    isLoadingInitialData,
    isLoadingMore,
    isLoadMore,
    loadMoreLibraryMedia,
    libraryMediaError,
    activeFilter,
    resetFilters,
    transitionInProgress = false
}) => {
    const { t } = useTranslation();

    if (isLoadingInitialData || transitionInProgress) {
        return (
            <div className="flex h-full items-center justify-center pb-5">
                <Loader className="h-5 w-5 text-icoBlue" />
            </div>
        );
    }

    if (libraryMediaError) {
        return (
            <div className="mt-3 px-6">
                <InfoBanner
                    title={t('errors:error')}
                    message={t('errors:error_occured_fetch_data')}
                />
            </div>
        );
    }

    return (
        <div className="scheduler-media-library h-full">
            <div className="grid grid-cols-2 gap-1.5 p-1.5 xl:grid-cols-3">
                <MediaUpload
                    button={
                        <Dropdown.Button
                            as="div"
                            className="flex h-full cursor-pointer items-center justify-center rounded-m bg-icoBlue-100 focus:ring-0 dark:bg-icoDarkMode-deepBlack"
                            name="media-add">
                            <Icon icon="media-upload" className="text-icoBlue" />
                        </Dropdown.Button>
                    }
                    dropdownPosition="left"
                    dropdownSize="sm"
                    classNameWrapper="square z-10"
                    itemStyle="small"
                    mutateLibraryMedia={mutateLibraryMedia}
                />
                {dataLibraryMedia[0].data.length > 0 &&
                    dataLibraryMedia.map(({ data }) => {
                        return data.map((item, index) => {
                            const media = item.medias[0] ?? null;
                            // For filter in media library used/unused
                            if (media) {
                                media.library_media_id = item?.id ?? null;
                            }

                            return (
                                <TooltipDisableSortableItem
                                    activeFilter={activeFilter}
                                    key={index}
                                    resetFilters={resetFilters}>
                                    <MediaItem
                                        disabled={activeFilter && activeFilter?.value !== 'all'}
                                        media={media}
                                        caption={item?.caption}
                                        id={item.id}
                                    />
                                </TooltipDisableSortableItem>
                            );
                        });
                    })}
            </div>
            {isLoadMore ? (
                <div className="flex w-full justify-center py-3">
                    <LoadMoreButton
                        isLoadingMore={isLoadingMore}
                        loadMoreItems={loadMoreLibraryMedia}
                        size="sm"
                    />
                </div>
            ) : null}
        </div>
    );
};

MediaList.propTypes = {
    dataLibraryMedia: PropTypes.array,
    isLoadingInitialData: PropTypes.bool,
    isLoadingMore: PropTypes.bool,
    isLoadMore: PropTypes.bool,
    loadMoreLibraryMedia: PropTypes.func,
    libraryMediaError: PropTypes.object,
    transitionInProgress: PropTypes.bool
};

const MediaLibraryScheduler = ({
    className,
    isFeedPreview = false,
    keyReloadUniqMediaIds = Date.now(),
    activeFilter = null,
    resetFilters = () => {
        /** */
    },
    size = SIZE_DEFAULT,
    onDestroy = () => {
        /** */
    },
    onClose = () => {
        /** */
    },
    transitionInProgress = false,
    ...restProps
}) => {
    useEffect(() => {
        return () => {
            onDestroy();
        };
    }, []);
    const { t } = useTranslation('scheduler');
    const { activeSocialProfile } = useActiveSocialProfile();
    const [filtersOpen, setFiltersOpen] = useState(false);

    const {
        dataLibraryMedia,
        isLoadingInitialData,
        isLoadingMore,
        isLoadMore,
        loadMoreLibraryMedia,
        libraryMediaError,
        setRequestParams,
        mutateLibraryMedia
    } = useLibraryMedia({
        activeSPID: activeSocialProfile.id,
        params: {
            global_media_types: true
        }
    });

    return (
        <div
            className={clsx(
                'flex h-full flex-col bg-white dark:bg-icoDarkMode-anthracite',
                className,
                {
                    'border-l border-icoGray-300 dark:border-icoDarkMode-beton': !isFeedPreview,
                    'rounded-t-md': isFeedPreview
                }
            )}
            {...restProps}>
            <div
                className={clsx(
                    'flex items-center justify-between border-b border-icoGray-300 px-5 dark:border-icoDarkMode-beton',
                    {
                        'py-3.5': size === SIZE_SMALL,
                        'py-5': size === SIZE_DEFAULT
                    }
                )}>
                <div
                    className={clsx('mr-2 flex items-center gap-x-2.5 text-sm', {
                        'xl:text-base': size === SIZE_DEFAULT
                    })}>
                    <Icon icon="media-library" className="text-icoBlue" />
                    <p>{t('media_library.your_media_library')}</p>
                </div>
                <div className="flex h-full items-center space-x-2.5">
                    <button
                        data-testid="button-toggle-media-library-filters"
                        className={clsx('flex items-center text-sm', {
                            'xl:text-base': size === SIZE_DEFAULT
                        })}
                        onClick={() => setFiltersOpen(!filtersOpen)}>
                        <Icon
                            icon="filter"
                            className={clsx({
                                'text-icoGray-500 dark:text-icoDarkMode-wolf': !filtersOpen,
                                'text-icoBlue': filtersOpen
                            })}
                        />
                    </button>
                    <span className="h-full w-px bg-icoGray-300 dark:bg-icoDarkMode-goudron" />
                    <button onClick={onClose} className="flex items-center">
                        <Icon
                            icon="close"
                            className="text-icoGray-500 dark:text-icoDarkMode-wolf"
                        />
                    </button>
                </div>
            </div>
            <Filters
                className={clsx({
                    hidden: !filtersOpen,
                    block: filtersOpen
                })}
                setRequestParams={setRequestParams}
            />
            <div className="scrollbar flex-1">
                <div className="flex h-full flex-col" key={keyReloadUniqMediaIds}>
                    <MediaList
                        dataLibraryMedia={dataLibraryMedia}
                        isLoadingInitialData={isLoadingInitialData}
                        isLoadingMore={isLoadingMore}
                        isLoadMore={isLoadMore}
                        activeFilter={activeFilter}
                        resetFilters={resetFilters}
                        loadMoreLibraryMedia={loadMoreLibraryMedia}
                        libraryMediaError={libraryMediaError}
                        mutateLibraryMedia={mutateLibraryMedia}
                        transitionInProgress={transitionInProgress}
                    />
                </div>
            </div>
        </div>
    );
};

MediaLibraryScheduler.propTypes = {
    mediaList: PropTypes.array,
    setUploadedMediaState: PropTypes.func,
    uploadRestrictions: PropTypes.object,
    onClose: PropTypes.func
};

export { ImageMedia, MediaLibraryScheduler };
